/**
 * Decides what an agency tenant's user is offered in the sidebar.
 *
 * Pure on purpose: the walkthrough on 2026-09-19 found an `account_manager`
 * landing on an HR dashboard offering Leave, Payroll, Attendance,
 * Recruitment, Expenses and Employees — six destinations that role cannot
 * use, every one of which answers 403. Keeping the decision in a tested
 * function rather than inline JSX is what stops that coming back.
 *
 * Icons are deliberately NOT here. This module answers "what may this person
 * reach", which is a permissions question; how it is drawn is the shell's
 * problem. That also keeps the module testable without a renderer.
 */

import { isAwaitingAccess } from '../auth/pending-access.logic.js';

export interface AgencyNavItem {
  to: string;
  label: string;
}

export interface AgencyNavGroup {
  label: string;
  items: AgencyNavItem[];
}

/** The module key that marks a tenant as an agency. Migration 0079 inserts it. */
export const AGENCY_MODULE_KEY = 'agency';

/*
 * The client-workspace redesign (spec 2026-09-26 §2–§3) replaced the
 * seven-tab agency board with one destination per job: an admin assigns
 * clients and sets limits, an account manager works their clients, a designer
 * works their tasks. Leads, Board, Intake and Finished files are no longer in
 * the daily path — their routes redirect to `/agency` — so they are not
 * offered here either. A link to a page that only redirects is noise.
 */
const CLIENTS: AgencyNavItem = { to: '/agency/clients', label: 'Clients' };
const MY_CLIENTS: AgencyNavItem = { to: '/agency/my-clients', label: 'My clients' };
const COMMITMENTS: AgencyNavItem = { to: '/agency/commitments', label: 'Commitments' };
const SETTINGS: AgencyNavItem = { to: '/agency/settings', label: 'Settings' };
const MY_TASKS: AgencyNavItem = { to: '/agency/tasks', label: 'My tasks' };

const STAFF_ITEMS: AgencyNavItem[] = [
  // The agency's own staff records. Reachable here because an agency runs on
  // people who are also employees: an account manager with no employee record
  // cannot be sent a notification at all — the alert resolves to nobody and
  // says so only in a log line — and this is the screen that fixes that.
  // Before this link, the only way in was to know the URL.
  { to: '/employees', label: 'People' },
  { to: '/payroll', label: 'Team & payroll' },
  { to: '/leave', label: 'Leave' },
  // User administration. Easy to leave out, and leaving it out is worse than
  // it looks: /admin/roles is the ONLY screen where an account can be given
  // a role or removed, so an agency owner without this link cannot manage
  // who can sign in at all. Caught by running the shell, not by reading it.
  { to: '/admin/roles', label: 'Users & roles' },
  /*
    Company Settings had NO link anywhere in the agency shell, which mattered
    more than it sounds: the Modules card lives there, and that card is the
    only way a tenant can switch the agency product on. A customer who signed
    up for it landed on an HR sidebar with no route forward that did not
    involve someone typing a URL.
  */
  { to: '/settings/company', label: 'Company settings' },
];

/**
 * Roles that run the agency as a business: they assign clients, set the
 * generation limits and still need the HR screens for their own staff.
 * `tenant_admin` is in here because it holds no agency role at all — it is the
 * owner's login at most tenants, and leaving it out was the failure mode that
 * sank the role-derived-only design. Matches the server's `isAdminRole`.
 */
const ADMIN_ROLES = ['agency_owner', 'tenant_admin', 'super_admin'];

/**
 * @returns the agency sidebar groups, or `null` when this tenant is not an
 * agency — in which case the caller renders the existing HR nav untouched.
 * Returning null rather than an empty array is the difference between "not an
 * agency" and "an agency with nothing to show", and only the first should
 * fall back.
 */
export function deriveAgencyNav(
  modules: readonly string[],
  roles: readonly string[],
): AgencyNavGroup[] | null {
  if (!modules.includes(AGENCY_MODULE_KEY)) return null;

  // A self-registered account holds only `member`, which grants nothing —
  // not even a read on an agency job. Handing it an agency shell sends it to
  // a board whose every request 403s, which reads as a broken product. It
  // belongs on the "waiting for access" screen instead, so: not an agency
  // user yet.
  if (isAwaitingAccess(roles)) return null;

  const has = (candidates: readonly string[]): boolean =>
    candidates.some((role) => roles.includes(role));
  const isAdmin = has(ADMIN_ROLES);

  const work: AgencyNavItem[] = [];
  if (isAdmin) {
    // An admin can also hold clients of their own (small agencies), so My
    // clients is offered alongside the roster; it is simply empty otherwise.
    work.push(CLIENTS, MY_CLIENTS, COMMITMENTS, SETTINGS);
  } else {
    if (has(['account_manager'])) work.push(MY_CLIENTS);
    if (has(['designer'])) work.push(MY_TASKS);
  }

  // Every new workspace endpoint answers 403 to anyone outside admin, AM and
  // designer (spec R.16), so such a user gets no agency links at all rather
  // than links that fail. An empty list, not null: they are still in an
  // agency tenant and must not fall back to the HR nav.
  const groups: AgencyNavGroup[] = work.length > 0 ? [{ label: 'Agency', items: work }] : [];

  // Staff administration is its own group rather than more entries under
  // "Agency", so that an owner reading the sidebar can tell the two jobs
  // apart at a glance — running the agency, versus running the business.
  if (isAdmin) {
    groups.push({ label: 'Staff', items: [...STAFF_ITEMS] });
  }

  return groups;
}
